export enum SubscriptionPlanType {
  FREE = "free",
  STARTER = "starter"
}

export interface PlanLimits {
  syncedDatasetsMax: number
  maxRowsPerTable: number
  projectsMax: number
  /** Cloud AI generation & schema-drift refresh (local MCP generation is always free). */
  aiDataGeneration: boolean
  cloudSync: boolean
  priorityProcessing: boolean
  prioritySupport: boolean
  serviceConnectors: boolean
}

export interface SubscriptionPlan {
  type: SubscriptionPlanType
  name: string
  currency: "USD"
  price: {
    monthly: number
    yearly: number
  }
  limits: PlanLimits
  targetUser: string
  description: string
  features: string[]
  ctaLabel: string
}

export const SUBSCRIPTION_PLANS: Record<SubscriptionPlanType, SubscriptionPlan> = {
  [SubscriptionPlanType.FREE]: {
    type: SubscriptionPlanType.FREE,
    name: "Free",
    currency: "USD",
    price: {
      monthly: 0,
      yearly: 0
    },
    limits: {
      syncedDatasetsMax: 0,
      maxRowsPerTable: 100,
      projectsMax: 3,
      aiDataGeneration: false,
      cloudSync: false,
      priorityProcessing: false,
      prioritySupport: false,
      serviceConnectors: false,
    },
    targetUser: "First-time users trying Seedmancer",
    description: "Everything you need to evaluate Seedmancer on real schemas.",
    features: [
      "Snapshot create & restore",
      "Unlimited local AI generation via MCP",
      "MCP server for AI agent integration",
      "Unlimited test data & revisions on your machine"
    ],
    ctaLabel: "Start for free"
  },
  [SubscriptionPlanType.STARTER]: {
    type: SubscriptionPlanType.STARTER,
    name: "Pro",
    currency: "USD",
    price: {
      monthly: 5,
      yearly: 50
    },
    limits: {
      syncedDatasetsMax: 200,
      maxRowsPerTable: 100_000,
      projectsMax: 3,
      aiDataGeneration: true,
      cloudSync: true,
      priorityProcessing: true,
      prioritySupport: true,
      serviceConnectors: true,
    },
    targetUser: "Solo developers, QA engineers, and indie teams",
    description: "Serious testing workloads with AI generation and cloud push & pull.",
    features: [
      "Everything in Free",
      "AI test data generation (seedmancer generate)",
      "Schema-drift refresh (seedmancer refresh)",
      "Cloud push & pull (up to 200 dataset versions)",
      "Priority support"
    ],
    ctaLabel: "Pro plan"
  }
}

export function getPlanByType(type: SubscriptionPlanType): SubscriptionPlan {
  return SUBSCRIPTION_PLANS[type]
}

export function getPlanLimits(type: SubscriptionPlanType): PlanLimits {
  return SUBSCRIPTION_PLANS[type].limits
}

export function formatPlanPrice(type: SubscriptionPlanType, isYearly: boolean): string {
  const plan = SUBSCRIPTION_PLANS[type]
  const amount = isYearly ? plan.price.yearly : plan.price.monthly
  if (amount === 0) return "$0"
  return `$${amount}`
}

export function formatPlanPeriod(isYearly: boolean): string {
  return isYearly ? "/year" : "/month"
}
